/**
 * Fast Storefront API loader for category-children section.
 * Fetches only the current category (+ children) and relevant collections.
 *
 * Debug: open DevTools Console on a category page.
 * Logs are ON by default. Disable: ?category_debug=0 or localStorage.removeItem('category_children_debug')
 * Force ON:  ?category_debug=1 or localStorage.setItem('category_children_debug','1')
 */
(function () {
  const DEBUG_PREFIX = '[Category Children]';

  function isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('category_debug') === '0') return false;
      if (params.get('category_debug') === '1') return true;
      if (localStorage.getItem('category_children_debug') === '0') return false;
      if (localStorage.getItem('category_children_debug') === '1') return true;
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function debugLog(label, data) {
    if (!isDebugEnabled()) return;
    if (data !== undefined) {
      console.log(DEBUG_PREFIX, label, data);
    } else {
      console.log(DEBUG_PREFIX, label);
    }
  }

  function debugWarn(label, data) {
    if (!isDebugEnabled()) return;
    if (data !== undefined) {
      console.warn(DEBUG_PREFIX, label, data);
    } else {
      console.warn(DEBUG_PREFIX, label);
    }
  }

  function getDomSnapshot() {
    const grids = Array.from(document.querySelectorAll('.category-children__grid'));
    const items = Array.from(
      document.querySelectorAll('.category-children__grid .category-children__item a[href]')
    );
    return {
      grids: grids.map((g) => ({
        id: g.id || '(no id)',
        display: g.style.display || getComputedStyle(g).display,
        itemCount: g.querySelectorAll('.category-children__item').length,
      })),
      links: items.map((a) => ({
        href: a.getAttribute('href'),
        title: (a.querySelector('.category-card__title') || {}).textContent?.trim() || '',
        isCollection: a.href.includes('/collections/'),
        isCategory: a.href.includes('/pages/categories/'),
      })),
      visibleCollectionCount: items.filter((a) => a.href.includes('/collections/')).length,
      visibleCategoryCount: items.filter((a) => a.href.includes('/pages/categories/')).length,
      loaderVisible:
        document.getElementById('category-children-loader')?.style.display === 'flex',
    };
  }

  function logCategoryChildrenDebug(phase) {
    if (!isDebugEnabled()) return;
    const serverData = getServerData();
    console.groupCollapsed(`${DEBUG_PREFIX} ${phase} — ${window.location.pathname}`);
    debugLog('Server decision (Liquid)', {
      categoryHandle: serverData.categoryHandle,
      currentCategoryId: serverData.currentCategoryId,
      currentCategoryName: serverData.currentCategoryName,
      serverRenderBranch: serverData.serverRenderBranch,
      showChildren: serverData.showChildren,
      showCollections: serverData.showCollections,
      showNotAvailable: serverData.showNotAvailable,
      expectedChildCount: serverData.expectedChildCount,
      foundChildCount: serverData.foundChildCount,
      currentHasCollection: serverData.currentHasCollection,
      collectionHandles: serverData.collectionHandles,
      childRefHandles: serverData.childRefHandles,
      foundCategoryIds: serverData.foundCategoryIds,
      needsEnhancement: serverData.needsEnhancement,
      hasStorefrontToken: serverData.hasStorefrontToken,
    });
    debugLog('DOM snapshot', getDomSnapshot());
    if (serverData.showCollections && serverData.expectedChildCount > 0) {
      debugWarn('BUG? Server chose COLLECTIONS but expectedChildCount > 0', {
        expectedChildCount: serverData.expectedChildCount,
        childRefHandles: serverData.childRefHandles,
      });
    }
    if (serverData.showChildren && serverData.expectedChildCount === 0) {
      debugWarn('BUG? Server chose CHILDREN but expectedChildCount is 0');
    }
    console.groupEnd();
  }

  window.logCategoryChildrenDebug = logCategoryChildrenDebug;

  function getServerData() {
    const el = document.getElementById('category-children-server-data');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function getCategoryHandle(serverData) {
    const urlParts = window.location.pathname.split('/').filter(Boolean);
    const categoriesIndex = urlParts.indexOf('categories');
    if (categoriesIndex >= 0 && urlParts.length > categoriesIndex + 1) {
      return urlParts[categoriesIndex + 1];
    }
    if (urlParts.length > 0) return urlParts[urlParts.length - 1];
    return serverData.categoryHandle || '';
  }

  function parseRichText(jsonString) {
    try {
      if (typeof jsonString !== 'string' || !jsonString.trim().startsWith('{')) {
        return jsonString;
      }
      const parsed = JSON.parse(jsonString);
      function renderNode(node) {
        if (!node || !node.type) return '';
        switch (node.type) {
          case 'root':
            return (node.children || []).map(renderNode).join('');
          case 'paragraph':
            return `<p>${(node.children || []).map(renderNode).join('')}</p>`;
          case 'text': {
            let text = node.value || '';
            if (node.bold) text = `<strong>${text}</strong>`;
            if (node.italic) text = `<em>${text}</em>`;
            if (node.underline) text = `<u>${text}</u>`;
            return text;
          }
          default:
            if (node.children) return node.children.map(renderNode).join('');
            return node.value || '';
        }
      }
      return renderNode(parsed);
    } catch (e) {
      return jsonString;
    }
  }

  function normalizeCategory(node) {
    const id = node.id.split('/').pop();
    return { id, handle: node.handle, fields: node.fields };
  }

  function getField(category, key) {
    return category.fields.find((f) => f.key === key);
  }

  function getFieldValue(category, key) {
    const field = getField(category, key);
    return field ? field.value : '';
  }

  function hasChildCategory(category, childId) {
    const field = getField(category, 'select_child_categories');
    if (!field) return false;
    if (field.references && field.references.edges) {
      return field.references.edges.some(
        (ref) => ref.node && ref.node.id && ref.node.id.split('/').pop() === childId
      );
    }
    if (field.value) {
      const valueStr = String(field.value);
      return (
        valueStr.includes(`Metaobject/${childId}`) ||
        valueStr.includes(`gid://shopify/Metaobject/${childId}`)
      );
    }
    return false;
  }

  function childHasSubcategories(child) {
    const field = getField(child, 'select_child_categories');
    if (!field) return false;
    if (field.references && field.references.edges && field.references.edges.length > 0) {
      return true;
    }
    if (field.value) {
      const valueStr = String(field.value).trim();
      return valueStr !== '' && valueStr !== '[]' && /Metaobject\/\d+/.test(valueStr);
    }
    return false;
  }

  function extractChildCategories(currentCategory, categoryDetailsMap) {
    const children = [];
    const field = getField(currentCategory, 'select_child_categories');
    if (!field) return children;

    if (field.references && field.references.edges) {
      field.references.edges.forEach((ref) => {
        const childId = ref.node.id.split('/').pop();
        const child = categoryDetailsMap.get(childId) || normalizeCategory(ref.node);
        categoryDetailsMap.set(childId, child);
        children.push(child);
      });
    } else if (field.value) {
      const gidPattern = /gid:\/\/shopify\/Metaobject\/(\d+)/g;
      let match;
      while ((match = gidPattern.exec(String(field.value))) !== null) {
        const child = categoryDetailsMap.get(match[1]);
        if (child) children.push(child);
      }
    }
    return children;
  }

  function serverBreadcrumbToCategory(data) {
    if (!data) return null;
    return {
      id: '',
      handle: data.handle,
      fields: [{ key: 'category_display_name', value: data.name }],
    };
  }

  async function initCategoryChildrenApi(config) {
    const serverData = getServerData();
    const token = config.token;
    const shopDomain = config.shopDomain;

    logCategoryChildrenDebug('api-init-start');

    if (!token || !shopDomain) {
      debugWarn('API skipped — missing token or shop domain');
      return;
    }

    const currentCategoryHandle = getCategoryHandle(serverData);
    if (!currentCategoryHandle) {
      debugWarn('API skipped — could not resolve category handle from URL');
      return;
    }

    const needsEnhancement = serverData.needsEnhancement === true;
    const renderedItemCount = document.querySelectorAll(
      '.category-children__grid .category-children__item'
    ).length;
    const showLoaderOnly =
      needsEnhancement &&
      (serverData.showNotAvailable === true ||
        !serverData.currentCategoryId ||
        renderedItemCount === 0);

    debugLog('API gate', {
      currentCategoryHandle,
      needsEnhancement,
      renderedItemCount,
      showLoaderOnly,
      serverShowChildren: serverData.showChildren,
      serverShowCollections: serverData.showCollections,
    });

    const loader = document.getElementById('category-children-loader');
    if (showLoaderOnly && loader) loader.style.display = 'flex';

    if (!needsEnhancement) {
      debugLog('API skipped — needsEnhancement is false (server render only)');
      if (loader) loader.style.display = 'none';
      logCategoryChildrenDebug('api-skipped');
      return;
    }

    const storefrontApiUrl = `https://${shopDomain}/api/2024-01/graphql.json`;

    async function storefrontFetch(query, variables) {
      const response = await fetch(storefrontApiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query, variables }),
      });
      if (!response.ok) return null;
      const data = await response.json();
      if (data.errors) return null;
      return data.data;
    }

    const CATEGORY_BY_HANDLE_QUERY = `
      query CategoryByHandle($handle: String!) {
        metaobject(handle: { type: "categories", handle: $handle }) {
          id
          handle
          fields {
            key
            value
            type
            ... on MetaobjectField {
              references(first: 50) {
                edges {
                  node {
                    ... on Metaobject {
                      id
                      handle
                      fields {
                        key
                        value
                        type
                        ... on MetaobjectField {
                          references(first: 10) {
                            edges {
                              node {
                                ... on Metaobject {
                                  id
                                }
                              }
                            }
                          }
                          reference {
                            ... on MediaImage {
                              image {
                                url
                                altText
                                width
                                height
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
              reference {
                ... on MediaImage {
                  image {
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    `;

    const COLLECTIONS_QUERY = `
      query CollectionsForCategory($first: Int!, $after: String, $query: String!) {
        collections(first: $first, after: $after, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              title
              image {
                url
                altText
              }
              products(first: 1) {
                edges {
                  node {
                    id
                  }
                }
              }
              metafields(identifiers: [{ namespace: "custom", key: "choose_category" }]) {
                id
                namespace
                key
                value
                type
                references(first: 10) {
                  edges {
                    node {
                      ... on Metaobject {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const PARENT_LOOKUP_QUERY = `
      query ParentLookup($type: String!, $first: Int!, $after: String) {
        metaobjects(type: $type, first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              fields {
                key
                value
                type
                ... on MetaobjectField {
                  references(first: 50) {
                    edges {
                      node {
                        ... on Metaobject {
                          id
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    let currentCategory = null;
    let currentCategoryId = null;
    let childCategories = [];
    let associatedCollections = [];
    let parentCategory = serverBreadcrumbToCategory(serverData.parent);
    let grandparentCategory = serverBreadcrumbToCategory(serverData.grandparent);
    let greatGrandparentCategory = serverBreadcrumbToCategory(serverData.greatGrandparent);

    const categoryData = await storefrontFetch(CATEGORY_BY_HANDLE_QUERY, {
      handle: currentCategoryHandle,
    });

    if (!categoryData || !categoryData.metaobject) {
      debugWarn('API failed — metaobject not found for handle', currentCategoryHandle);
      if (loader) loader.style.display = 'none';
      return;
    }

    currentCategory = normalizeCategory(categoryData.metaobject);
    currentCategoryId = currentCategory.id;

    const selectChildFieldRaw = getField(currentCategory, 'select_child_categories');
    debugLog('API — current category loaded', {
      id: currentCategoryId,
      handle: currentCategory.handle,
      name: getFieldValue(currentCategory, 'category_display_name'),
      selectChildCategories: {
        hasReferences: !!(selectChildFieldRaw?.references?.edges?.length),
        referenceCount: selectChildFieldRaw?.references?.edges?.length || 0,
        valuePreview: selectChildFieldRaw?.value
          ? String(selectChildFieldRaw.value).slice(0, 200)
          : null,
      },
    });

    const categoryDetailsMap = new Map();
    categoryDetailsMap.set(currentCategoryId, currentCategory);

    categoryData.metaobject.fields.forEach((field) => {
      if (field.references && field.references.edges) {
        field.references.edges.forEach((edge) => {
          if (edge.node && edge.node.id) {
            const child = normalizeCategory(edge.node);
            categoryDetailsMap.set(child.id, child);
          }
        });
      }
    });

    const underConstructionField = getField(currentCategory, 'under_construction');
    const isUnderConstruction =
      underConstructionField &&
      (underConstructionField.value === true ||
        underConstructionField.value === 'true' ||
        String(underConstructionField.value).toLowerCase() === 'true');

    if (isUnderConstruction) {
      renderUnderConstructionAPI();
      if (loader) loader.style.display = 'none';
      return;
    }

    // Preserve the order children were entered in select_child_categories
    // (extractChildCategories already returns them in reference order).
    childCategories = extractChildCategories(currentCategory, categoryDetailsMap);

    debugLog('API — raw child categories (before filter)', childCategories.map((c) => ({
      id: c.id,
      handle: c.handle,
      name: getFieldValue(c, 'category_display_name'),
      hasSubcategories: childHasSubcategories(c),
    })));

    async function findParentOf(childId) {
      let hasNextPage = true;
      let cursor = null;
      while (hasNextPage) {
        const data = await storefrontFetch(PARENT_LOOKUP_QUERY, {
          type: 'categories',
          first: 50,
          after: cursor,
        });
        if (!data || !data.metaobjects) return null;

        for (const edge of data.metaobjects.edges) {
          const category = normalizeCategory(edge.node);
          if (hasChildCategory(category, childId)) return category;
        }

        hasNextPage = data.metaobjects.pageInfo.hasNextPage;
        cursor = data.metaobjects.pageInfo.endCursor;
      }
      return null;
    }

    async function resolveParentChain() {
      if (!parentCategory) {
        parentCategory = await findParentOf(currentCategoryId);
      }
      if (parentCategory && !grandparentCategory) {
        grandparentCategory = await findParentOf(parentCategory.id);
      }
      if (grandparentCategory && !greatGrandparentCategory) {
        greatGrandparentCategory = await findParentOf(grandparentCategory.id);
      }
    }

    function collectionMatchesCategory(collection, categoryId) {
      const metafieldsArray = Array.isArray(collection.metafields)
        ? collection.metafields.filter(Boolean)
        : collection.metafields
          ? [collection.metafields]
          : [];

      for (const metafield of metafieldsArray) {
        if (metafield.references && metafield.references.edges) {
          for (const ref of metafield.references.edges) {
            if (!ref || !ref.node || !ref.node.id) continue;
            const refId = ref.node.id;
            let metaobjectId = refId;
            if (refId.includes('Metaobject/')) {
              metaobjectId = refId.split('Metaobject/').pop().split('/').pop();
            } else if (refId.includes('/')) {
              metaobjectId = refId.split('/').pop();
            }
            if (
              metaobjectId === categoryId ||
              refId === `gid://shopify/Metaobject/${categoryId}`
            ) {
              return true;
            }
          }
        }

        if (metafield.value) {
          let valueStr = String(metafield.value);
          try {
            if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
              const parsed = JSON.parse(valueStr);
              if (Array.isArray(parsed)) valueStr = parsed.join(',');
            }
          } catch (e) {
            /* use raw value */
          }
          valueStr = valueStr.replace(/[\[\]"]/g, '');
          if (
            valueStr.includes(`Metaobject/${categoryId}`) ||
            valueStr.includes(`gid://shopify/Metaobject/${categoryId}`) ||
            valueStr === categoryId
          ) {
            return true;
          }
        }
      }
      return false;
    }

    async function fetchCollectionsForCategory(categoryId) {
      const gid = `gid://shopify/Metaobject/${categoryId}`;
      const queryVariants = [
        `metafields.custom.choose_category:${gid}`,
        `metafields.custom.choose_category:"${gid}"`,
      ];

      for (const queryStr of queryVariants) {
        const results = [];
        let hasNextPage = true;
        let cursor = null;
        let queryWorked = false;

        while (hasNextPage) {
          const data = await storefrontFetch(COLLECTIONS_QUERY, {
            first: 50,
            after: cursor,
            query: queryStr,
          });
          if (!data || !data.collections) break;

          queryWorked = true;
          data.collections.edges.forEach((edge) => {
            const collection = edge.node;
            if (collectionMatchesCategory(collection, categoryId)) {
              results.push(collection);
            }
          });

          hasNextPage = data.collections.pageInfo.hasNextPage;
          cursor = data.collections.pageInfo.endCursor;
        }

        if (queryWorked && results.length > 0) return results;
      }

      return fetchCollectionsFallbackScan(categoryId);
    }

    const COLLECTIONS_PAGE_QUERY = `
      query CollectionsPage($first: Int!, $after: String) {
        collections(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              handle
              title
              image {
                url
                altText
              }
              products(first: 1) {
                edges {
                  node {
                    id
                  }
                }
              }
              metafields(identifiers: [{ namespace: "custom", key: "choose_category" }]) {
                id
                namespace
                key
                value
                type
                references(first: 10) {
                  edges {
                    node {
                      ... on Metaobject {
                        id
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    async function fetchCollectionsFallbackScan(categoryId) {
      const results = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const data = await storefrontFetch(COLLECTIONS_PAGE_QUERY, {
          first: 50,
          after: cursor,
        });
        if (!data || !data.collections) break;

        data.collections.edges.forEach((edge) => {
          const collection = edge.node;
          if (collectionMatchesCategory(collection, categoryId)) {
            results.push(collection);
          }
        });

        hasNextPage = data.collections.pageInfo.hasNextPage;
        cursor = data.collections.pageInfo.endCursor;
      }

      return results;
    }

    async function filterDisplayableChildren(children) {
      const checks = await Promise.all(
        children.map(async (child) => {
          const hasSubs = childHasSubcategories(child);
          if (hasSubs) {
            debugLog(`Child kept (has subcategories): ${child.handle}`, { id: child.id });
            return child;
          }
          const cols = await fetchCollectionsForCategory(child.id);
          const kept = cols.length > 0;
          debugLog(`Child ${kept ? 'kept' : 'FILTERED OUT'} (collection check): ${child.handle}`, {
            id: child.id,
            collectionCount: cols.length,
            collectionHandles: cols.map((c) => c.handle),
          });
          return kept ? child : null;
        })
      );
      const filtered = checks.filter(Boolean);
      debugLog('API — children after filterDisplayableChildren', {
        before: children.length,
        after: filtered.length,
        kept: filtered.map((c) => c.handle),
        removed: children
          .filter((c) => !filtered.find((f) => f.id === c.id))
          .map((c) => c.handle),
      });
      return filtered;
    }

    await resolveParentChain();

    const hasChildren = childCategories.length > 0;

    if (hasChildren) {
      childCategories = await filterDisplayableChildren(childCategories);
    } else {
      debugWarn('API — no child categories on metaobject; will fetch collections for CURRENT category', {
        currentCategoryId,
        handle: currentCategoryHandle,
      });
      associatedCollections = await fetchCollectionsForCategory(currentCategoryId);
      debugLog('API — collections for current category', {
        count: associatedCollections.length,
        handles: associatedCollections.map((c) => c.handle),
      });
    }

    updatePageHeaderAPI(currentCategory);
    buildBreadcrumbsAPI();

    const existingGridItems = document.querySelectorAll(
      '.category-children__grid .category-children__item'
    );
    const hasServerContent = existingGridItems.length > 0;

    let renderPath = 'none';
    if (hasServerContent && serverData.showCollections) {
      renderPath = 'mergeCollectionsIntoGrid (server already showCollections)';
      mergeCollectionsIntoGrid(associatedCollections);
    } else if (hasServerContent && serverData.showChildren) {
      renderPath = 'mergeChildrenIntoGrid (server showChildren)';
      mergeChildrenIntoGrid(childCategories);
    } else {
      renderPath =
        childCategories.length > 0
          ? 'renderContentAPI → CHILDREN'
          : associatedCollections.length > 0
            ? 'renderContentAPI → COLLECTIONS'
            : 'renderContentAPI → empty/not available';
      debugLog('API — renderContentAPI decision', {
        renderPath,
        childCount: childCategories.length,
        collectionCount: associatedCollections.length,
      });
      renderContentAPI();
    }

    debugLog('API — final render path', { renderPath, hasServerContent });
    logCategoryChildrenDebug('api-complete');
    debugLog('DOM after API', getDomSnapshot());

    if (loader) loader.style.display = 'none';

    function updatePageHeaderAPI(categoryData) {
      let pageWidth = document.querySelector('.category-children .page-width');
      if (!pageWidth) return;

      let pageHeader = pageWidth.querySelector('.page-header');
      if (!pageHeader) {
        pageHeader = document.createElement('div');
        pageHeader.className = 'page-header';
        pageWidth.insertBefore(pageHeader, pageWidth.firstChild);
      }

      const categoryName =
        getFieldValue(categoryData, 'category_display_name') ||
        categoryData.handle.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      const description = getFieldValue(categoryData, 'meta_description') || '';

      let titleElement = pageHeader.querySelector('.page-header__title');
      if (!titleElement) {
        titleElement = document.createElement('h1');
        titleElement.className = 'page-header__title';
        pageHeader.appendChild(titleElement);
      }
      titleElement.textContent = categoryName;

      if (description) {
        let descWrap = pageHeader.querySelector('[data-read-more-wrap]');
        let descElement = pageHeader.querySelector('.read-more-description, .page-header__description');
        let toggle = pageHeader.querySelector('.read-more-link');

        const parseFn = window.parseCategoryRichText || parseRichText;
        const fullHtml = parseFn(description);

        if (!descWrap) {
          descWrap = document.createElement('div');
          descWrap.className = 'page-header__description-wrap read-more-wrap is-pending';
          descWrap.setAttribute('data-read-more-wrap', '');

          descElement = document.createElement('div');
          descElement.className = 'page-header__description rte read-more-description';
          descElement.dataset.limit = '500';

          toggle = document.createElement('span');
          toggle.className = 'read-more-link page-header__read-more-toggle';
          toggle.hidden = true;
          toggle.tabIndex = 0;
          toggle.setAttribute('role', 'button');
          toggle.textContent = 'Read more';

          descWrap.appendChild(descElement);
          descWrap.appendChild(toggle);
          titleElement.insertAdjacentElement('afterend', descWrap);
        } else {
          descElement = descWrap.querySelector('.read-more-description, .page-header__description');
          toggle = descWrap.querySelector('.read-more-link');
        }

        if (descElement) {
          descElement.innerHTML = fullHtml;
          descElement.dataset.full = fullHtml;
          descElement.classList.add('rich-text-ready');
        }

        if (descWrap) {
          delete descWrap.dataset.readMoreInit;
          descWrap.classList.add('is-pending');
          descWrap.classList.remove('is-ready');
        }

        if (typeof window.initReadMoreDescriptions === 'function') {
          window.initReadMoreDescriptions(pageHeader);
        }
      }
    }

    function buildBreadcrumbsAPI() {
      if (!parentCategory && !grandparentCategory && !greatGrandparentCategory) return;

      let pageHeader = document.querySelector('.page-header');
      if (!pageHeader) return;

      const currentCategoryName =
        getFieldValue(currentCategory, 'category_display_name') ||
        currentCategoryHandle.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

      let breadcrumbHTML = '<nav class="category-breadcrumb" aria-label="Breadcrumb">';

      if (greatGrandparentCategory) {
        const name =
          getFieldValue(greatGrandparentCategory, 'category_display_name') ||
          greatGrandparentCategory.handle;
        breadcrumbHTML += `<a href="/pages/categories/${greatGrandparentCategory.handle}" class="category-breadcrumb__link">${name}</a><span class="bread-seprator">/</span>`;
      }
      if (grandparentCategory) {
        const name =
          getFieldValue(grandparentCategory, 'category_display_name') || grandparentCategory.handle;
        breadcrumbHTML += `<a href="/pages/categories/${grandparentCategory.handle}" class="category-breadcrumb__link">${name}</a><span class="bread-seprator">/</span>`;
      }
      if (parentCategory) {
        const name = getFieldValue(parentCategory, 'category_display_name') || parentCategory.handle;
        breadcrumbHTML += `<a href="/pages/categories/${parentCategory.handle}" class="category-breadcrumb__link">${name}</a><span class="bread-seprator">/</span>`;
      }

      breadcrumbHTML += `<span class="category-breadcrumb__current">${currentCategoryName}</span></nav>`;

      const existingBreadcrumb = pageHeader.querySelector('.category-breadcrumb');
      if (existingBreadcrumb) {
        existingBreadcrumb.outerHTML = breadcrumbHTML;
      } else {
        pageHeader.insertAdjacentHTML('afterbegin', breadcrumbHTML);
      }
    }

    function renderUnderConstructionAPI() {
      let comingSoonTemplate = document.querySelector('.coming-soon-template');
      if (comingSoonTemplate) return;

      const gridContainer = document.querySelector('.category-children__grid');
      const emptySection = document.querySelector('.category-children__empty');
      const pageHeader = document.querySelector('.page-header');

      if (gridContainer) gridContainer.style.display = 'none';
      if (emptySection) emptySection.style.display = 'none';
      if (pageHeader) pageHeader.style.display = 'none';

      const section =
        document.querySelector('.category-children') || document.querySelector('.section');
      if (!section) return;

      comingSoonTemplate = document.createElement('div');
      comingSoonTemplate.className = 'coming-soon-template';
      const innerContainer = document.createElement('div');
      innerContainer.className = 'container';

      buildBreadcrumbsAPI();
      const breadcrumb = document.querySelector('.category-breadcrumb');
      if (breadcrumb) innerContainer.appendChild(breadcrumb.cloneNode(true));

      const heading = document.createElement('h1');
      heading.className = 'heading';
      heading.textContent =
        getFieldValue(currentCategory, 'category_display_name') || currentCategoryHandle;
      innerContainer.appendChild(heading);

      const description = document.createElement('div');
      description.className = 'description';
      description.innerHTML =
        'Something amazing is coming soon. Stay tuned for updates! <div class="contact-info" style="margin-top: 1.5rem;"><p>If you have any query, please call us at <a href="tel:+12096297113">+1 (209) 629-7113</a> or contact us at <a href="mailto:john@gearhousehydraulics.com">john@gearhousehydraulics.com</a>.</p></div>';
      innerContainer.appendChild(description);

      comingSoonTemplate.appendChild(innerContainer);
      section.insertAdjacentElement('beforebegin', comingSoonTemplate);
    }

    function getExistingHrefs() {
      const hrefs = new Set();
      document.querySelectorAll('.category-children__grid .category-children__item a[href]').forEach((a) => {
        hrefs.add(a.getAttribute('href'));
      });
      return hrefs;
    }

    function getOrCreateApiGrid() {
      let grid =
        document.getElementById('category-children-grid-api') ||
        document.getElementById('category-children-grid-categories') ||
        document.getElementById('category-children-grid-collections');

      if (!grid) {
        const pageWidth = document.querySelector('.category-children .page-width');
        if (!pageWidth) return null;
        grid = document.createElement('div');
        grid.id = 'category-children-grid-api';
        grid.className = `category-children__grid grid grid--${serverData.columnsDesktop}-col-desktop grid--${serverData.columnsMobile}-col-tablet-down`;
        pageWidth.appendChild(grid);
      }

      grid.style.display = '';
      return grid;
    }

    function renderCategoryCard(gridContainer, category) {
      const categoryName =
        getFieldValue(category, 'category_display_name') ||
        category.handle.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
      const categoryUrl = `/pages/categories/${category.handle}`;
      const description = getFieldValue(category, 'meta_description') || '';

      const imageField = getField(category, 'category_image');
      let imageUrl = '';
      if (imageField && imageField.reference && imageField.reference.image) {
        imageUrl = imageField.reference.image.url || '';
      }

      const categoryItem = document.createElement('div');
      categoryItem.className = 'category-children__item grid__item';

      const categoryCard = document.createElement('a');
      categoryCard.href = categoryUrl;
      categoryCard.className =
        'category-card category-card--subcategories category-card--clickable';

      const cardContent = document.createElement('div');
      cardContent.className = 'category-card__content';

      const cardTitle = document.createElement('h3');
      cardTitle.className = 'category-card__title';
      cardTitle.textContent = categoryName;
      cardContent.appendChild(cardTitle);

      if (description) {
        const cardDescription = document.createElement('div');
        cardDescription.className = 'category-card__description';
        cardDescription.textContent = description;
        cardContent.appendChild(cardDescription);
      }

      categoryCard.appendChild(cardContent);

      if (imageUrl && imageUrl.startsWith('http')) {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'category-card__image-wrapper';
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = categoryName;
        img.className = 'category-card__image';
        img.loading = 'lazy';
        imageWrapper.appendChild(img);
        categoryCard.appendChild(imageWrapper);
      }

      categoryItem.appendChild(categoryCard);
      gridContainer.appendChild(categoryItem);
    }

    function renderCollectionCardAPI(gridContainer, collection) {
      const collectionItem = document.createElement('div');
      collectionItem.className = 'category-children__item grid__item collection-card';

      const collectionCard = document.createElement('a');
      collectionCard.href = `/collections/${collection.handle}`;
      collectionCard.className = 'category-card category-card--collection category-card--clickable';

      const cardContent = document.createElement('div');
      cardContent.className = 'category-card__content';
      const cardTitle = document.createElement('h3');
      cardTitle.className = 'category-card__title';
      cardTitle.textContent = collection.title;
      cardContent.appendChild(cardTitle);
      collectionCard.appendChild(cardContent);

      if (collection.image && collection.image.url) {
        const imageWrapper = document.createElement('div');
        imageWrapper.className = 'category-card__image-wrapper';
        const img = document.createElement('img');
        img.src = collection.image.url;
        img.alt = collection.image.altText || collection.title;
        img.className = 'category-card__image';
        img.loading = 'lazy';
        imageWrapper.appendChild(img);
        collectionCard.appendChild(imageWrapper);
      }

      collectionItem.appendChild(collectionCard);
      gridContainer.appendChild(collectionItem);
    }

    function mergeChildrenIntoGrid(children) {
      const grid = getOrCreateApiGrid();
      if (!grid) return;
      const existingHrefs = getExistingHrefs();
      children.forEach((category) => {
        const url = `/pages/categories/${category.handle}`;
        if (!existingHrefs.has(url)) {
          renderCategoryCard(grid, category);
          existingHrefs.add(url);
        }
      });
      const emptySection = document.getElementById('category-children-empty');
      if (emptySection && grid.children.length > 0) emptySection.style.display = 'none';
    }

    function mergeCollectionsIntoGrid(collections) {
      const grid = getOrCreateApiGrid();
      if (!grid) return;
      const existingHrefs = getExistingHrefs();
      collections.forEach((collection) => {
        const url = `/collections/${collection.handle}`;
        if (!existingHrefs.has(url)) {
          renderCollectionCardAPI(grid, collection);
          existingHrefs.add(url);
        }
      });
    }

    function renderContentAPI() {
      const emptySection = document.getElementById('category-children-empty');
      if (emptySection) emptySection.style.display = 'none';

      const grid = getOrCreateApiGrid();
      if (!grid) return;

      if (
        !serverData.showChildren &&
        !serverData.showCollections &&
        document.querySelectorAll('.category-children__grid .category-children__item').length === 0
      ) {
        grid.innerHTML = '';
      }

      const existingHrefs = getExistingHrefs();

      if (childCategories.length > 0) {
        childCategories.forEach((category) => {
          const url = `/pages/categories/${category.handle}`;
          if (!existingHrefs.has(url)) {
            renderCategoryCard(grid, category);
            existingHrefs.add(url);
          }
        });
      } else if (associatedCollections.length > 0) {
        associatedCollections.forEach((collection) => {
          const url = `/collections/${collection.handle}`;
          if (!existingHrefs.has(url)) {
            renderCollectionCardAPI(grid, collection);
            existingHrefs.add(url);
          }
        });
      } else if (emptySection) {
        emptySection.style.display = '';
      }
    }
  }

  window.initCategoryChildrenApi = initCategoryChildrenApi;

  document.addEventListener('DOMContentLoaded', function () {
    logCategoryChildrenDebug('dom-ready');
  });
})();
